import * as React from 'react';
import { styled } from '@linaria/react';
import { Play, Pause } from 'lucide-react';

import Demo from '@/components/Demo';
import PrimaryButton from '@/components/ButtonPlayful/ButtonPrimary';
import Spinner from '@/components/Spinner';

import {
  BALL_CONTAINER_SIZE,
  useSimulateWork,
} from './CssVsJs.helpers';
import BallKeyframe from './BallKeyframe';
import BallRaf from './BallRaf';
import BallMotion from './BallMotion';
import BallGsap from './BallGsap';

const DEMO_MAP = {
  keyframe: {
    Ball: BallKeyframe,
    label: 'Using CSS Keyframes',
    isAffectedByWork: false,
  },
  raf: {
    Ball: BallRaf,
    label: 'Using a JavaScript loop',
    isAffectedByWork: true,
  },
  motion: {
    Ball: BallMotion,
    label: 'Using the Motion library',
    isAffectedByWork: false,
  },
  gsap: {
    Ball: BallGsap,
    label: 'Using GSAP',
    isAffectedByWork: true,
  },
};

interface Props {
  demoIds: Array<keyof typeof DEMO_MAP>;
}

function CssVsJsDemo({ demoIds }: Props) {
  const outerWrapperRef = React.useRef<HTMLDivElement>(null);

  const [isPlaying, setIsPlaying] = React.useState(false);
  const [ballKey, setBallKey] = React.useState(0);

  // IntersectionObserver to pause when component is out of view, since this component freezes the main thread periodically. It would be REALLY BAD to have this keep going. 😅
  React.useEffect(() => {
    const element = outerWrapperRef.current;
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting && isPlaying) {
          setIsPlaying(false);
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [isPlaying]);

  const { isSimulatingWork, setIsSimulatingWork } =
    useSimulateWork(isPlaying);

  const togglePlayPause = React.useCallback(() => {
    if (isPlaying) {
      setIsPlaying(false);
      setIsSimulatingWork(false);
    } else {
      setIsPlaying(true);
      setBallKey((prev) => prev + 1);
    }
  }, [isPlaying, setIsSimulatingWork]);

  return (
    <OuterWrapper ref={outerWrapperRef}>
      <Demo
        initialValues={{}}
        outputWrapper={null}
        customGrid={{
          gridTemplateColumns: '1fr',
          justifyItems: 'center',
        }}
        controls={[
          <Button
            key="play-pause-btn"
            onClick={togglePlayPause}
            discouraged={isSimulatingWork}
            Icon={
              isSimulatingWork ? Spinner : isPlaying ? Pause : Play
            }
          >
            {isSimulatingWork
              ? 'Simulating work…'
              : isPlaying
                ? 'Pause'
                : 'Play'}
          </Button>,
        ]}
        controlPosition="bottom"
      >
        {() => (
          <Wrapper>
            {demoIds.map((demoId) => {
              const { Ball, label, isAffectedByWork } =
                DEMO_MAP[demoId];

              return (
                <Cell key={demoId}>
                  <Label>{label}</Label>
                  <BallWrapper
                    data-is-busy={
                      isSimulatingWork && isAffectedByWork
                    }
                  >
                    <Ball key={ballKey} isPlaying={isPlaying} />
                  </BallWrapper>
                </Cell>
              );
            })}
          </Wrapper>
        )}
      </Demo>
    </OuterWrapper>
  );
}

const OuterWrapper = styled.div`
  position: relative;
`;

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  width: fit-content;
  margin-inline: auto;
`;

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--color-gray-300);
  border-radius: 8px;
  min-height: 120px;
`;

const BallWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: ${BALL_CONTAINER_SIZE}px;
  padding: 2px;
  box-sizing: content-box;
  border: 2px solid var(--wham-primary-300);
  border-radius: 1000px;

  &[data-is-busy='true'] {
    opacity: 0.5;
  }
`;

const Label = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
  text-align: center;
`;

const Button = styled(PrimaryButton)`
  width: 100%;
  max-width: 20rem;
`;

export default CssVsJsDemo;
